/**
 * LoginPage — tela de entrada do GeoGuard UPM.
 *
 * Único método de autenticação: SSO Microsoft Azure AD.
 * PINs hardcoded do protótipo foram removidos.
 */

import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/lib/supabase';
import JFBrasilLogo from '@/ui/JFBrasilLogo';
import { eventosApp } from '@/services/datasources/eventos-app';

const DEV_USERS = [
  { email: 'admin@jfbrasil.com.br',      label: 'Admin' },
  { email: 'engenheiro@jfbrasil.com.br', label: 'Engenheiro' },
  { email: 'vigilante1@upm.com.br',      label: 'Vigilante 1' },
  { email: 'vigilante2@upm.com.br',      label: 'Vigilante 2' },
] as const;

export default function LoginPage() {
  const { session, loading, signIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const sessionExpired = (location.state as { expired?: boolean } | null)?.expired;
  const [devError, setDevError] = useState<string | null>(null);
  const [devSubmitting, setDevSubmitting] = useState(false);

  async function signInAsDev(email: string) {
    setDevError(null);
    setDevSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password: 'devdev123' });
    // C-81: senha recusada NÃO é registrada. Login por senha só existe aqui, no
    // ambiente dev; a RPC anônima deixou de aceitar `login_falha_credenciais`
    // porque, em produção, ela só servia para gravar texto livre na trilha.
    if (error) setDevError(error.message);
    setDevSubmitting(false);
  }

  /**
   * GUPM-335: a tentativa é registrada antes do redirecionamento — se a pessoa
   * desistir na tela da Microsoft, é o único rastro de que tentou. `registrar`
   * tem teto de espera, então o redirecionamento atrasa no máximo esse teto.
   */
  async function entrarComMicrosoft() {
    await eventosApp.registrar('login_iniciado', { provedor: 'azure' });
    await signIn();
  }

  // Já autenticado → redireciona para o app
  useEffect(() => {
    if (!loading && session) {
      navigate('/', { replace: true });
    }
  }, [session, loading, navigate]);

  return (
    <div className="min-h-screen bg-jf-bg flex items-center justify-center p-6">
      <div className="w-full max-w-sm">

        {/* Card de login — tudo dentro do card */}
        <div
          className="bg-jf-surface border border-jf-border rounded-2xl p-8 relative"
          style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.4)' }}
        >
          {/* Linha amarela no topo */}
          <div
            className="absolute top-0 left-8 right-8 h-0.5 rounded-b"
            style={{ background: 'linear-gradient(90deg, transparent, #FECB00, transparent)' }}
          />

          {/* Logo JF Brasil */}
          <div className="flex justify-center mb-5">
            <JFBrasilLogo width={144} height={42} />
          </div>

          {/* Título e subtítulo */}
          <div className="text-center mb-6">
            <h1 className="text-white font-bold text-2xl">GeoGuard UPM</h1>
            <p className="text-slate-400 text-sm mt-1">
              Monitoramento Geotécnico — Barragens A, B e C
            </p>
          </div>

          {sessionExpired && (
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg px-4 py-3 mb-4">
              <p className="text-amber-400 text-sm text-center">
                Sessão expirada. Faça login novamente.
              </p>
            </div>
          )}

          <p className="text-slate-300 text-sm text-center mb-5">
            Acesse com sua conta corporativa<br />JF Brasil Engenharia
          </p>

          <button
            onClick={entrarComMicrosoft}
            disabled={loading}
            className="w-full flex items-center justify-center gap-3 bg-white hover:bg-slate-100 hover:-translate-y-px hover:shadow-[0_2px_12px_rgba(0,0,0,0.2)] active:translate-y-0 active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed text-slate-800 font-semibold text-sm px-4 py-3 rounded-lg transition-all duration-150"
          >
            <svg width="20" height="20" viewBox="0 0 21 21" fill="none" aria-hidden="true">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            Entrar com conta Microsoft
          </button>

          {/* Rodapé */}

          {import.meta.env.DEV && (
            <div className="mt-6 pt-5 border-t border-dashed border-jf-border">
              <p className="text-[10px] uppercase tracking-wider text-jf-dim text-center mb-3">
                🔧 Login dev (local)
              </p>
              <div className="grid grid-cols-2 gap-2">
                {DEV_USERS.map((u) => (
                  <button
                    key={u.email}
                    onClick={() => signInAsDev(u.email)}
                    disabled={devSubmitting || loading}
                    className="text-xs bg-jf-border/50 hover:bg-jf-border text-slate-300 hover:text-white px-3 py-2 rounded transition-colors disabled:opacity-50"
                  >
                    {u.label}
                  </button>
                ))}
              </div>
              {devError && (
                <p className="text-red-400 text-[10px] text-center mt-2">{devError}</p>
              )}
            </div>
          )}

        </div>
        <p className="text-slate-600 text-xs text-center mt-5">
          Somente contas <strong className="text-slate-500">@jfbr.com.br</strong> autorizadas<br />
          Login seguro via Azure AD · SSO corporativo
        </p>
      </div>
    </div>
  );
}
